import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Iconify from '@/components/Iconify';
import { useGetAccess } from '@/hooks/useAccess';
import {
  GlobalSearchCategory,
  GlobalSearchPermissions,
  GlobalSearchResult,
  GlobalSearchResultType,
  searchGlobalEntities,
} from './globalSearch';

type GlobalSearchDialogProps = {
  autoFocus?: boolean;
  debounceMs?: number;
  handleClose?: VoidFunction;
  minSearchChars?: number;
};

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_SEARCH_CHARS = 2;

const CATEGORY_ICON: Record<GlobalSearchResultType, string> = {
  client: 'eva:person-fill',
  provider: 'eva:briefcase-fill',
  booking: 'eva:calendar-fill',
  booking_session: 'eva:clock-fill',
};

const flattenResults = (categories: GlobalSearchCategory[] = []) =>
  categories.flatMap((category) => category.results);

export default function GlobalSearchDialog({
  autoFocus = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  handleClose,
  minSearchChars = DEFAULT_MIN_SEARCH_CHARS,
}: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const clientAccess = useGetAccess('clients');
  const providerAccess = useGetAccess('providers');
  const bookingAccess = useGetAccess('bookings');
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  const permissions: GlobalSearchPermissions = useMemo(
    () => ({
      canSearchClients: clientAccess.canView,
      canSearchProviders: providerAccess.canView,
      canSearchBookings: bookingAccess.canView,
    }),
    [bookingAccess.canView, clientAccess.canView, providerAccess.canView]
  );

  useEffect(() => {
    const nextSearchValue = inputValue.trim();

    if (nextSearchValue.length < minSearchChars) {
      setDebouncedInputValue('');
      return undefined;
    }

    const timeout = window.setTimeout(
      () => setDebouncedInputValue(nextSearchValue),
      debounceMs
    );

    return () => window.clearTimeout(timeout);
  }, [debounceMs, inputValue, minSearchChars]);

  const {
    data: categories = [],
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['globalSearch', debouncedInputValue, permissions],
    queryFn: () =>
      searchGlobalEntities({
        term: debouncedInputValue,
        permissions,
      }),
    enabled: debouncedInputValue.length >= minSearchChars,
    keepPreviousData: true,
  });

  const allResults = useMemo(() => flattenResults(categories), [categories]);
  const hasQuery = inputValue.trim().length >= minSearchChars;
  const hasResults = allResults.length > 0;
  const hasCategoryFeedback = categories.some(
    (category) => category.results.length > 0 || category.error
  );

  const handleSelectResult = (result: GlobalSearchResult) => {
    navigate(result.href);
    handleClose?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || allResults.length === 0) return;

    event.preventDefault();
    handleSelectResult(allResults[0]);
  };

  return (
    <Box sx={{ bgcolor: 'background.paper' }}>
      <TextField
        id="global-search-input"
        autoFocus={autoFocus}
        fullWidth
        placeholder="Search..."
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={handleKeyDown}
        variant="filled"
        InputProps={{
          startAdornment: (
            <Iconify
              icon="eva:search-fill"
              sx={{ color: 'text.disabled', width: 20, height: 20, mr: 1 }}
            />
          ),
          endAdornment: (
            <Stack direction="row" alignItems="center" spacing={1}>
              {isFetching && <CircularProgress size={18} />}
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'grey.600',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 0.5,
                  fontWeight: 'bold',
                }}
              >
                Esc
              </Typography>
            </Stack>
          ),
          disableUnderline: true,
          sx: {
            bgcolor: 'background.paper',
            p: 1,
          },
        }}
      />

      {hasQuery && (
        <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
          {isError && (
            <Alert severity="error" sx={{ m: 2 }}>
              Search failed
            </Alert>
          )}

          {!isFetching && !hasResults && !hasCategoryFeedback && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No results found
            </Typography>
          )}

          {categories
            .filter((category) => category.results.length > 0 || category.error)
            .map((category, categoryIndex) => (
              <Box key={category.type}>
                {categoryIndex > 0 && <Divider />}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, pt: 1.5, pb: 0.75 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {category.label}
                  </Typography>
                  {category.results.length > 0 && (
                    <Chip
                      size="small"
                      label={category.results.length}
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>

                {category.error ? (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ px: 2, pb: 1.5 }}
                  >
                    {category.error}
                  </Typography>
                ) : (
                  <List disablePadding>
                    {category.results.map((result) => (
                      <ListItemButton
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleSelectResult(result)}
                        sx={{ px: 2, py: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Iconify
                            icon={CATEGORY_ICON[result.type]}
                            sx={{ color: 'text.secondary' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          primary={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="subtitle2">
                                {result.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                #{result.id}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.25}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                              >
                                {result.subtitle}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {result.meta}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
}
